/**
 * "Don't Miss Twice" Streak Calculation
 * 
 * Based on James Clear's "Never Miss Twice" philosophy:
 * - The first mistake is never the one that ruins a habit
 * - It's the spiral of repeated mistakes that follows
 * - A single missed day is a "grace day" that doesn't break the streak
 * - Two consecutive missed days breaks the streak
 * 
 * Rest days and shield days count as covered days (not gaps).
 */

import type { EvidenceLog } from '@/types/database'

export interface StreakResult {
  /** The current streak count (days with actual completions or rest/shield days) */
  streak: number
  /** Whether the user is currently in a grace period (missed yesterday but streak is alive) */
  isInGracePeriod: boolean
  /** Total grace days (single missed days) within the active streak chain */
  graceDaysUsed: number
}

/**
 * Format a Date as YYYY-MM-DD in local timezone
 */
function formatDateLocal(date: Date): string {
  return date.toLocaleDateString('en-CA') // en-CA gives YYYY-MM-DD
}

/**
 * Get a Date for N days ago (at noon to avoid DST issues)
 */
function daysAgo(n: number): Date {
  const d = new Date()
  d.setHours(12, 0, 0, 0)
  d.setDate(d.getDate() - n)
  return d
}

/**
 * Calculate the global streak with "Don't Miss Twice" grace rule.
 * 
 * Algorithm:
 * 1. Find the most recent "covered" day (completion, rest, or shield)
 * 2. Walk backward through dates from that starting point
 * 3. Allow up to 1 consecutive missed day as a "grace day"
 * 4. If 2 consecutive days are missed, the streak breaks
 * 5. Covered days (completions + rest + shield) increment the streak count
 * 6. Grace days preserve the chain but don't increment the count
 */
export function calculateGlobalStreakWithGrace(
  logs: EvidenceLog[],
  restDayDates: Set<string> = new Set(),
  shieldDayDates: Set<string> = new Set()
): StreakResult {
  if (logs.length === 0) return { streak: 0, isInGracePeriod: false, graceDaysUsed: 0 }

  // Get unique completion dates (in local timezone)
  const completionDates = new Set<string>()
  logs.forEach(log => {
    if (
      log.ai_verdict === 'approved_gold' ||
      log.ai_verdict === 'approved_silver' ||
      log.ai_verdict === 'quick_complete'
    ) {
      const date = formatDateLocal(new Date(log.created_at))
      completionDates.add(date)
    }
  })

  if (completionDates.size === 0) return { streak: 0, isInGracePeriod: false, graceDaysUsed: 0 }

  // Check if a date is "covered" (completion, rest day, or shield day)
  const isCovered = (dateStr: string): boolean => {
    return completionDates.has(dateStr) ||
           restDayDates.has(dateStr) ||
           shieldDayDates.has(dateStr)
  }

  const todayStr = formatDateLocal(daysAgo(0))
  const yesterdayStr = formatDateLocal(daysAgo(1))
  const dayBeforeYesterdayStr = formatDateLocal(daysAgo(2))

  // Determine starting point for the streak walk-back
  // "Don't Miss Twice": streak is alive if most recent covered day is
  // today, yesterday, or day-before-yesterday (yesterday being a grace day)
  let startDate: string
  let isInGracePeriod = false

  if (isCovered(todayStr)) {
    startDate = todayStr
  } else if (isCovered(yesterdayStr)) {
    startDate = yesterdayStr
    // Today isn't over yet, so not in grace — just haven't completed today
  } else if (isCovered(dayBeforeYesterdayStr)) {
    // Day before yesterday is covered, yesterday is a grace day,
    // and today isn't over yet — streak is still alive
    startDate = dayBeforeYesterdayStr
    isInGracePeriod = true
  } else {
    // No covered day in the last 3 days: streak is broken
    // (yesterday + day before = 2 consecutive misses)
    return { streak: 0, isInGracePeriod: false, graceDaysUsed: 0 }
  }

  // Walk backward from start date, counting the streak
  let streak = 0
  let consecutiveGaps = 0
  let graceDaysUsed = 0
  const currentDate = new Date(startDate + 'T12:00:00') // noon to avoid DST issues

  for (let i = 0; i < 366; i++) {
    const dateStr = formatDateLocal(currentDate)

    if (isCovered(dateStr)) {
      streak++
      consecutiveGaps = 0
    } else {
      consecutiveGaps++
      if (consecutiveGaps >= 2) {
        // "Don't Miss Twice" — two consecutive misses breaks the streak
        break
      }
      graceDaysUsed++
    }

    currentDate.setDate(currentDate.getDate() - 1)
  }

  // Also detect grace period when today is covered but yesterday wasn't
  // (the grace day was used to bridge yesterday's gap)
  if (isCovered(todayStr) && !isCovered(yesterdayStr) && streak > 1) {
    isInGracePeriod = true
  }

  return { streak, isInGracePeriod, graceDaysUsed }
}

/**
 * Quick check: is the user currently in a "Don't Miss Twice" grace period?
 * 
 * Returns true when:
 * - The user has completions in recent history (active streak)
 * - Yesterday had no completions (grace day is in use)
 * - The streak is preserved because it hasn't been 2 consecutive misses
 * 
 * This is a lightweight check for UI banners — doesn't need rest/shield data.
 */
export function isInGracePeriodQuick(allLogs: EvidenceLog[]): boolean {
  if (allLogs.length === 0) return false

  const completionDates = new Set<string>()
  allLogs.forEach(log => {
    if (
      log.ai_verdict === 'approved_gold' ||
      log.ai_verdict === 'approved_silver' ||
      log.ai_verdict === 'quick_complete'
    ) {
      completionDates.add(formatDateLocal(new Date(log.created_at)))
    }
  })

  const todayStr = formatDateLocal(daysAgo(0))
  const yesterdayStr = formatDateLocal(daysAgo(1))
  const dayBeforeYesterdayStr = formatDateLocal(daysAgo(2))

  const completedToday = completionDates.has(todayStr)
  const completedYesterday = completionDates.has(yesterdayStr)
  const completedDBY = completionDates.has(dayBeforeYesterdayStr)

  // Grace period: yesterday was missed but streak is alive
  // Case 1: Today completed, yesterday missed (grace used for yesterday)
  if (completedToday && !completedYesterday && completedDBY) return true
  // Case 2: Today not yet completed, yesterday missed, DBY completed
  if (!completedToday && !completedYesterday && completedDBY) return true

  return false
}

/**
 * Get supportive comeback message based on streak and grace state.
 */
export function getGraceMessage(streak: number, graceDaysUsed: number): string {
  if (streak === 0) return ''
  
  const messages = [
    `Your ${streak}-day streak is safe! One off day won't break your chain — just don't miss twice.`,
    `Yesterday was a pause, not a stop. Your ${streak}-day streak lives on — show up today!`,
    `Missing one day doesn't define you. Your ${streak}-day streak is proof of who you are.`,
    `A brief rest, not a reset. Your ${streak}-day chain is intact — keep going!`,
    `One day off is recovery. Your ${streak}-day streak is still burning — fan the flame today!`,
  ]

  // Use a deterministic "random" based on streak to avoid message flicker
  const index = streak % messages.length
  return messages[index]
}
